import { Maybe } from 'graphql/jsutils/Maybe'
import * as R from 'ramda'
import { useCallback } from 'react'
import { firstValueFrom } from 'rxjs'

import useLogAndCaptureError from '../../../../hooks/useLogAndCaptureError'
import { useCartRemoveProduct } from '../../../cart/hooks/useCartRemoveProduct'
import { cartSettled$ } from '../../../cart/state/cart-mutation'
import { EScriptParamsLoader, EScriptUpdateCartAction } from '../../../escript/types'
import { isEmptyCartAction } from '../../../escript/utils'
import { setDispensaryCartState } from '../../events/cart'

export type UseEmptyCart = [emptyCart: () => Promise<void>]

export interface UseEmptyCartOptions {
  eScriptParamsLoader: EScriptParamsLoader
}

const shouldEmptyCart = (updateCartActions: Maybe<EScriptUpdateCartAction[]>) =>
  updateCartActions && updateCartActions.some(isEmptyCartAction)

export const useEmptyCart = ({ eScriptParamsLoader }: UseEmptyCartOptions): UseEmptyCart => {
  const { loadParams } = eScriptParamsLoader

  const [removeProductFromCart, { error }] = useCartRemoveProduct()

  useLogAndCaptureError(error)

  const emptyCart = useCallback(async (): Promise<void> => {
    // read the settled cart rather than the cart context's one-shot deferred, which resolves once
    // and then caches: this runs first in the hydration, so a stale item list empties the wrong cart.
    // `cartSettled$` rather than `cartMutationTarget$` because an absent cart is nothing to empty
    // here, not a failure.
    const [{ updateCartActions }, { cart }] = await Promise.all([
      loadParams(),
      firstValueFrom(cartSettled$),
    ])

    if (!shouldEmptyCart(updateCartActions)) {
      return
    }
    if (!cart || !cart.items || cart.items.length === 0) {
      return
    }
    setDispensaryCartState({ type: 'cart_empty_start' })
    return cart.items
      .reduce(async (acc, item) => {
        await acc
        const id = item?.id
        // Skip null lines: Number(undefined) is NaN, which serializes to null against the
        // non-null `itemId: Int!` and rejects the whole empty-cart chain into a cart-error state.
        if (id == null) {
          return
        }
        await removeProductFromCart({
          variables: {
            itemId: Number(id),
          },
        })
      }, Promise.resolve())
      .then(R.tap(() => setDispensaryCartState({ type: 'cart_empty_complete' })))
  }, [loadParams, removeProductFromCart])

  return [emptyCart]
}
